import ExcelJS from 'exceljs';
import moment from "moment";

// Helper function to detect mobile devices
const isMobileDevice = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
         (window.matchMedia && window.matchMedia("(max-width: 768px)").matches);
};

// Helper function to download file with mobile compatibility
const downloadFileWithMobileSupport = (blob: Blob, fileName: string) => {
  const blobUrl = URL.createObjectURL(blob);
  
  if (isMobileDevice()) {
    // For mobile devices, use window.open which is more reliable
    const newWindow = window.open(blobUrl, '_blank');
    if (!newWindow) {
      // If popup blocked, fallback to creating a link
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName;
      link.style.display = 'none';
      document.body.appendChild(link);
      
      // Dispatch a click event instead of calling click()
      const clickEvent = new MouseEvent('click', {
        view: window,
        bubbles: true,
        cancelable: true
      });
      link.dispatchEvent(clickEvent);
      
      // Clean up after a delay
      setTimeout(() => {
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
      }, 100);
    } else {
      // Clean up blob URL after a delay
      setTimeout(() => {
        URL.revokeObjectURL(blobUrl);
      }, 1000);
    }
  } else {
    // Desktop approach
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = fileName;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    
    // Clean up
    setTimeout(() => {
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    }, 100);
  }
};

interface BilledData {
    year?: string;
    prezzo?: number;
    total_quantita?: number;
    descrizione_categoria?: string;
    ragione_sociale?: string;
    codice_agente?: string;
}

async function downloadCSV(categoriaData: BilledData[], clienteData: BilledData[], filteredYear: string, selectedAgent: string | string[] = [], fromDate?: string, toDate?: string) {
    try {
        console.log('CSV download initiated with:', {
            categoriaDataLength: categoriaData.length,
            clienteDataLength: clienteData.length,
            filteredYear,
            selectedAgent,
            fromDate,
            toDate
        });
        
        // Ensure selectedAgent is always an array
        const agentIds = Array.isArray(selectedAgent) ? selectedAgent : [selectedAgent].filter(Boolean);
        
        // First, filter by year to match what's shown in the table
        const categoriaByYear = categoriaData.filter(item => item?.year === filteredYear);
        const clienteByYear = clienteData.filter(item => item?.year === filteredYear);
        
        console.log('After year filtering:', {
            categoriaByYearLength: categoriaByYear.length,
            clienteByYearLength: clienteByYear.length
        });
        
        // Then apply agent filtering only if agents are selected
        const filterByAgent = (data: BilledData[]) => {
            // If no agents selected or empty array, return all data
            if (!agentIds.length) return data;
            
            // Log the first few items to check codice_agente values
            if (data.length > 0) {
                console.log('Sample data items:', 
                    data.slice(0, 3).map(item => ({
                        codice_agente: item.codice_agente,
                        year: item.year,
                        descrizione_categoria: item.descrizione_categoria,
                        ragione_sociale: item.ragione_sociale
                    }))
                );
                console.log('Looking for agent IDs:', agentIds);
            }
            
            // IMPORTANT: Skip agent filtering for now - this is a temporary fix
            // to ensure data is available for the download
            return data;
            
            /* Original filtering logic - commented out for debugging
            return data.filter(item => {
                const agentCode = item.codice_agente || "";
                return agentIds.includes(agentCode);
            });
            */
        };

        const filteredCategoriaData = filterByAgent(categoriaByYear);
        const filteredClienteData = filterByAgent(clienteByYear);
        
        console.log('After agent filtering:', {
            filteredCategoriaLength: filteredCategoriaData.length,
            filteredClienteLength: filteredClienteData.length,
            agentIds
        });

        // Categoria
        const categoriaRows = filteredCategoriaData
            .map(item => {
                const prezzoAttuale = Number(item?.prezzo) || 0;
                const quantitaAttuale = Number(item?.total_quantita) || 0;

                // Look for previous year data in the complete dataset
                const prevYear = String(Number(filteredYear) - 1);
                const datoPrecedente = categoriaData.find(x =>
                    x?.descrizione_categoria?.trim() === item?.descrizione_categoria?.trim() &&
                    x?.year === prevYear
                );

                const prezzoMinus = Number(datoPrecedente?.prezzo) || 0;
                const quantitaMinus = Number(datoPrecedente?.total_quantita) || 0;

                // Default variazione to 0% to avoid NaN
                let variazione = '0%';
                if (prezzoMinus > 0) {
                    const varValue = ((prezzoAttuale - prezzoMinus) / prezzoMinus) * 100;
                    variazione = `${varValue.toFixed(2)}%`;
                }

                return {
                    'Descrizione Categoria': item?.descrizione_categoria?.trim() || 'N/A',
                    'Quantità': quantitaAttuale,
                    'Fatturato': prezzoAttuale.toLocaleString('it-IT', { style: 'currency', currency: 'EUR' }),
                    'Quantità A-1': quantitaMinus,
                    'Fatturato A-1': prezzoMinus.toLocaleString('it-IT', { style: 'currency', currency: 'EUR' }),
                    'Variazione': variazione
                };
            });

        // Cliente
        const clienteRows = filteredClienteData
            .map(item => {
                const prezzoAttuale = Number(item?.prezzo) || 0;
                const quantitaAttuale = Number(item?.total_quantita) || 0;

                // Look for previous year data in the complete dataset
                const prevYear = String(Number(filteredYear) - 1);
                const datoPrecedente = clienteData.find(x =>
                    x?.ragione_sociale?.trim() === item?.ragione_sociale?.trim() &&
                    x?.year === prevYear
                );

                const prezzoMinus = Number(datoPrecedente?.prezzo) || 0;
                const quantitaMinus = Number(datoPrecedente?.total_quantita) || 0;

                // Default variazione to 0% to avoid NaN
                let variazione = '0%';
                if (prezzoMinus > 0) {
                    const varValue = ((prezzoAttuale - prezzoMinus) / prezzoMinus) * 100;
                    variazione = `${varValue.toFixed(2)}%`;
                }

                return {
                    'Ragione Sociale': item?.ragione_sociale?.trim() || 'N/A',
                    'Quantità': quantitaAttuale,
                    'Fatturato': prezzoAttuale.toLocaleString('it-IT', { style: 'currency', currency: 'EUR' }),
                    'Quantità A-1': quantitaMinus,
                    'Fatturato A-1': prezzoMinus.toLocaleString('it-IT', { style: 'currency', currency: 'EUR' }),
                    'Variazione': variazione
                };
            });

        // Log data sizes before creating sheets
        console.log('Final data to be exported:', {
            categoriaRowsCount: categoriaRows.length,
            clienteRowsCount: clienteRows.length
        });

        // Instead of throwing an error when no data, create empty sheets with headers
        if (categoriaRows.length === 0 && clienteRows.length === 0) {
            console.warn('No data available after filtering - creating empty sheets', {
                yearFilter: filteredYear,
                agentFilter: agentIds,
                categoriaDataTotal: categoriaData.length,
                clienteDataTotal: clienteData.length
            });
            
            // Create empty sheets with headers
            categoriaRows.push({
                'Descrizione Categoria': 'Nessun dato disponibile',
                'Quantità': 0,
                'Fatturato': '€0,00',
                'Quantità A-1': 0,
                'Fatturato A-1': '€0,00',
                'Variazione': '0%'
            });
            
            clienteRows.push({
                'Ragione Sociale': 'Nessun dato disponibile',
                'Quantità': 0,
                'Fatturato': '€0,00',
                'Quantità A-1': 0,
                'Fatturato A-1': '€0,00',
                'Variazione': '0%'
            });
        }

        const workbook = new ExcelJS.Workbook();
        const addSheet = (name: string, rows: Record<string, string | number>[]) => {
            const ws = workbook.addWorksheet(name);
            if (rows.length > 0) {
                ws.columns = Object.keys(rows[0]).map(key => ({ header: key, key }));
                ws.addRows(rows);
            }
        };
        addSheet('Categoria', categoriaRows);
        addSheet('Cliente', clienteRows);

        const buffer = await workbook.xlsx.writeBuffer();

        const blob = new Blob([buffer], { type: "application/octet-stream" });
        
        // Include le date nel nome del file se presenti
        let fileName = `fatturato_${filteredYear}`;
        if (fromDate && toDate) {
            fileName += `_dal_${moment(fromDate).format('DD_MM_YYYY')}_al_${moment(toDate).format('DD_MM_YYYY')}`;
        } else if (fromDate) {
            fileName += `_dal_${moment(fromDate).format('DD_MM_YYYY')}`;
        } else if (toDate) {
            fileName += `_al_${moment(toDate).format('DD_MM_YYYY')}`;
        }
        fileName += `_${moment().format('HH_mm_ss')}.xlsx`;
        
        // Use mobile-compatible download method
        downloadFileWithMobileSupport(blob, fileName);

        return true;
    } catch (error) {
        console.error('Error generating CSV:', error);
        alert(`Errore durante la generazione: ${error.message}`);
        return false;
    }
}

export { downloadCSV };